import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  adminWalletAdd,
  adminWalletRemove,
  getAdminWalletHistory,
} from "../../services/adminWalletService";
import { searchUsers } from "../../services/userService";

const toastCfg = { autoClose: 1500 };

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Detail Modal ─── */
const AddWalletModal = ({ show, onHide, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [userId, setUserId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [addType, setAddType] = useState("credit");

  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = React.useRef(null);

  // reset when modal opens, load initial list
  useEffect(() => {
    if (show) {
      setAmount("");
      setUserId("");
      setRemarks("");
      setAddType("credit");
      setUserQuery("");
      setSelectedUser(null);
      setDropdownOpen(false);
      setUsers([]);
      searchUsers("").then((res) => {
        if (res?.status) setUsers(res.data || []);
      });
    }
  }, [show]);

  // debounced server search as user types
  const handleQueryChange = (e) => {
    const val = e.target.value;
    setUserQuery(val);
    setUserId("");
    setDropdownOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchUsers(val);
        if (res?.status) setUsers(res.data || []);
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  const filteredUsers = users;

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    setUserId(String(u.id));
    setUserQuery(u.name + (u.mobile ? `  •  ${u.mobile}` : ""));
    setDropdownOpen(false);
  };

  const handleSubmit = async () => {
    if (!userId || !amount) {
      toast.error("User ID & Amount required", toastCfg);
      return;
    }
    
    try {
      let res;
      if (addType === "credit") {
        res = await adminWalletAdd({
          user_id: userId,
          amount,
          remarks: remarks || "Admin added balance",
        });
      } else {
        res = await adminWalletRemove({
          user_id: userId,
          amount,
          remarks: remarks || "Admin removed balance",
        });
      }

      if (res?.status) {
        toast.success(
          addType === "credit"
            ? "Wallet credited successfully"
            : "Wallet debited successfully",
          toastCfg
        );
        onSuccess();
        onHide();
      } else {
        toast.error("Transaction failed", toastCfg);
      }
    } catch {
      toast.error("Server error", toastCfg);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>Wallet Management</div>
          <div style={ms.headerSub}>Credit or Debit User Balances</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>
      <div style={ms.body}>
        <div style={ms.grid}>
          <div style={{ gridColumn: "1 / -1", position: "relative" }}>
            <label style={ms.label}>User</label>

            {/* Search input — hidden once a user is selected */}
            {!selectedUser && (
              <>
                <input
                  style={ms.input}
                  placeholder="Search by name, mobile or ID…"
                  value={userQuery}
                  onChange={handleQueryChange}
                  onFocus={() => setDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                  autoComplete="off"
                />
                {dropdownOpen && (searching || filteredUsers.length > 0) && (
                  <div style={ms.dropdown}>
                    {searching && (
                      <div style={{ padding: "9px 14px", color: "#94a3b8", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                        Searching…
                      </div>
                    )}
                    {!searching && filteredUsers.map((u) => (
                      <div
                        key={u.id}
                        style={ms.dropdownItem}
                        onMouseDown={() => handleSelectUser(u)}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fdf4")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                      >
                        <span style={{ fontWeight: 600, color: "#1e293b" }}>{u.name || "—"}</span>
                        <span style={{ fontSize: "0.74rem", color: "#94a3b8", marginLeft: 8 }}>
                          {u.mobile || ""}{"  "}
                          <span style={{ color: "#cbd5e1" }}>#{u.id}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Selected user card */}
            {selectedUser && (
              <div style={ms.userCard}>
                <div style={ms.userCardAvatar}>
                  <Icon icon="solar:user-bold-duotone" style={{ fontSize: 22, color: "#16a34a" }} />
                </div>
                <div style={ms.userCardInfo}>
                  <div style={ms.userCardName}>{selectedUser.name || "—"}</div>
                  <div style={ms.userCardMeta}>
                    {selectedUser.mobile && (
                      <span style={ms.userCardMetaItem}>
                        <Icon icon="solar:phone-bold" style={{ fontSize: 11, marginRight: 3 }} />
                        {selectedUser.mobile}
                      </span>
                    )}
                    <span style={ms.userIdBadge}>ID #{selectedUser.id}</span>
                  </div>
                </div>
                <button
                  style={ms.userCardClear}
                  onClick={() => {
                    setSelectedUser(null);
                    setUserId("");
                    setUserQuery("");
                    setDropdownOpen(false);
                  }}
                  title="Change user"
                >
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 13 }} />
                </button>
              </div>
            )}
          </div>
          <div>
            <label style={ms.label}>Amount</label>
            <input
              style={ms.input}
              placeholder="Enter Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div>
            <label style={ms.label}>Type</label>
            <select
              style={ms.select}
              value={addType}
              onChange={(e) => setAddType(e.target.value)}
            >
              <option value="credit">Credit</option>
              <option value="debit">Debit</option>
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>Remarks</label>
            <input
              style={ms.input}
              placeholder="Remarks (Optional)"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div style={ms.footer}>
        <button style={ms.cancelBtn} onClick={onHide}>Cancel</button>
        <button 
          style={addType === "credit" ? ms.approveBtn : ms.rejectBtn} 
          onClick={handleSubmit}
        >
          {addType === "credit" ? "Add Credit" : "Remove Balance"}
        </button>
      </div>
    </Modal>
  );
};

/* ─── Modal styles ─── */
const ms = {
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9",
  },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub:   { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: {
    background: "#f1f5f9", border: "none", borderRadius: 8,
    width: 32, height: 32, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b",
  },
  body:  { padding: "18px 24px" },
  grid:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 18px" },
  label: {
    display: "block", fontSize: "0.75rem", fontWeight: 700,
    color: "#475569", marginBottom: 5,
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  input: {
    width: "100%", padding: "9px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc",
    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  },
  select: {
    width: "100%", padding: "9px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit", boxSizing: "border-box",
  },
  footer: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9",
  },
  cancelBtn: {
    padding: "8px 18px", background: "#f1f5f9", border: "none",
    borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem",
    fontWeight: 600, color: "#475569", cursor: "pointer",
  },
  approveBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer",
  },
  rejectBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 20px", background: "linear-gradient(135deg,#991b1b,#dc2626)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer",
  },
  dropdown: {
    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1050,
    background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 9,
    boxShadow: "0 4px 16px rgba(0,0,0,0.10)", maxHeight: 220, overflowY: "auto",
    marginTop: 2,
  },
  dropdownItem: {
    padding: "9px 14px", cursor: "pointer", background: "#fff",
    borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center",
    flexWrap: "wrap", gap: 2, transition: "background 0.1s",
  },
  userCard: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "10px 14px", background: "#f0fdf4",
    border: "1.5px solid #bbf7d0", borderRadius: 10, marginTop: 2,
  },
  userCardAvatar: {
    width: 38, height: 38, borderRadius: "50%",
    background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  userCardInfo: { flex: 1, minWidth: 0 },
  userCardName: { fontWeight: 700, fontSize: "0.88rem", color: "#14532d" },
  userCardMeta: { display: "flex", alignItems: "center", gap: 8, marginTop: 3, flexWrap: "wrap" },
  userCardMetaItem: { fontSize: "0.75rem", color: "#64748b", display: "flex", alignItems: "center" },
  userIdBadge: {
    display: "inline-flex", alignItems: "center",
    padding: "2px 8px", background: "#16a34a", color: "#fff",
    borderRadius: 100, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.03em",
  },
  userCardClear: {
    background: "#fee2e2", border: "none", borderRadius: "50%",
    width: 26, height: 26, cursor: "pointer", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626",
  },
};

/* ─── Page styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub: { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem" },
  addBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 18px", background: "linear-gradient(135deg,#16632b,#1da144)",
    border: "none", borderRadius: 9, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer",
    boxShadow: "0 2px 6px rgba(22,99,43,0.3)"
  },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 10,
  },
  toolbarLeft:  { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },
  searchWrap:  { position: "relative", display: "flex", alignItems: "center" },
  searchIcon:  { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: {
    padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.82rem", color: "#334155", background: "#f8fafc",
    outline: "none", width: 220, fontFamily: "inherit",
  },
  clearBtn: {
    position: "absolute", right: 8, background: "none", border: "none",
    cursor: "pointer", color: "#94a3b8", padding: 0,
    display: "flex", alignItems: "center",
  },
  select: {
    padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },
  tableWrap: { overflowX: "auto" },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: {
    padding: "10px 14px", background: "#f8fafc", color: "#64748b",
    fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase",
    letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9",
    whiteSpace: "nowrap", textAlign: "left",
  },
  td: {
    padding: "9px 14px", borderBottom: "1px solid #f8fafc",
    color: "#334155", verticalAlign: "middle",
  },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 8,
  },
  pgInfo:      { fontSize: "0.78rem", color: "#64748b" },
  pgBtns:      { display: "flex", gap: 4 },
  pgBtn: {
    minWidth: 30, height: 30, border: "1px solid #e2e8f0",
    background: "#fff", borderRadius: 7, cursor: "pointer",
    fontSize: "0.78rem", color: "#475569", fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  },
  pgBtnActive: {
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", fontWeight: 700,
  },
};

/* ─── Table cell styles ─── */
const ts = {
  uid:    { fontSize: "0.76rem", color: "#94a3b8", fontFamily: "monospace" },
  name:   { fontWeight: 600, color: "#1e293b", fontSize: "0.83rem" },
  wallet: { fontWeight: 700, color: "#16632b", fontSize: "0.83rem" },
  badge: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px", borderRadius: 100,
    fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap", textTransform: "capitalize",
  },
};

export const AdminWalletHistory = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [type, setType] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  
  const [showModal, setShowModal] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAdminWalletHistory();
      if (res?.status) {
        setData(res.history || []);
      } else {
        toast.error("Failed to load wallet history", toastCfg);
      }
    } catch {
      toast.error("Server error", toastCfg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filteredData = useMemo(() => {
    let result = data;
    if (type !== "all") {
      result = result.filter((i) => i.txn_type?.toLowerCase() === type);
    }
    return result;
  }, [data, type]);

  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 44,
      enableSorting: false,
      cell: ({ row }) => row.index + 1 + pagination.pageIndex * pagination.pageSize,
    },
    {
      accessorKey: "id",
      header: "ID",
      size: 70,
      cell: ({ getValue }) => <span style={ts.uid}>#{getValue()}</span>,
    },
    {
      accessorKey: "name",
      header: "User",
      cell: ({ getValue }) => <span style={ts.name}>{getValue() || "—"}</span>,
    },
    {
      accessorKey: "mobile",
      header: "Mobile",
      cell: ({ getValue }) => <span style={{ color: "#475569" }}>{getValue() || "—"}</span>,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ getValue }) => (
        <span style={ts.wallet}>₹{Number(getValue() || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      accessorKey: "txn_type",
      header: "Type",
      cell: ({ getValue }) => {
        const t = (getValue() || "").toLowerCase();
        return (
          <span style={{ 
            ...ts.badge, 
            background: t === "credit" ? "#dcfce7" : "#fee2e2", 
            color: t === "credit" ? "#16a34a" : "#dc2626" 
          }}>
            {t}
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const s = (getValue() || "").toLowerCase();
        return (
          <span style={{ 
            ...ts.badge, 
            background: s === "approved" ? "#dcfce7" : s === "rejected" ? "#fee2e2" : "#fef9c3", 
            color: s === "approved" ? "#16a34a" : s === "rejected" ? "#dc2626" : "#92400e" 
          }}>
            {s}
          </span>
        );
      },
    },
    {
      accessorKey: "remarks",
      header: "Remarks",
      cell: ({ getValue }) => <span>{getValue() || "—"}</span>,
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: ({ getValue }) => <span style={{ color: "#64748b" }}>{getValue()}</span>,
    },
  ], [pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return (
        row.original.name?.toLowerCase().includes(v) ||
        row.original.mobile?.toLowerCase().includes(v) ||
        String(row.original.id).includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();

  return (
    <div style={s.root}>
      <div style={s.headerWrap}>
        <div>
          <h4 style={s.pageTitle}>Admin Wallet Management</h4>
          <p style={s.pageSub}>Credit or Debit user wallets and view transaction history</p>
        </div>
        <button style={s.addBtn} onClick={() => setShowModal(true)}>
          <Icon icon="solar:wallet-add-bold" style={{ fontSize: 18 }} />
          Add Credit / Debit
        </button>
      </div>

      <div style={s.card}>
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search name, mobile or ID…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            <select style={s.select} value={type}
              onChange={(e) => { setType(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="all">All Type</option>
              <option value="credit">Credit</option>
              <option value="debit">Debit</option>
            </select>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:wallet-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No wallet history matched
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{pageIndex * pageSize + 1}–{Math.min((pageIndex + 1) * pageSize, filteredData.length)}</strong>
            {" "}of <strong>{filteredData.length}</strong> records
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}          disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}            disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const pg = Math.max(0, Math.min(pageIndex - 3, pageCount - 7)) + i;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      <AddWalletModal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        onSuccess={loadHistory} 
      />
    </div>
  );
};